import { useState, useEffect, useRef } from 'react';
import { useApp } from '../App';
import api from '../api';
import { useI18n } from '../i18n/index.jsx';

export default function AiAssistant() {
  const { setPage } = useApp();
  const { t } = useI18n();

  const SUGGESTIONS = [
    { label: `📄 ${t('ai.suggestion_create_invoice')}`, text: t('ai.suggestion_create_invoice_text') },
    { label: `📊 ${t('ai.suggestion_zus_report')}`, text: t('ai.suggestion_zus_report_text') },
    { label: `💰 ${t('ai.suggestion_balance')}`, text: t('ai.suggestion_balance_text') },
    { label: `👤 ${t('ai.suggestion_hire_employee')}`, text: t('ai.suggestion_hire_text') },
    { label: `📋 ${t('ai.suggestion_employee_list')}`, text: t('ai.suggestion_list_text') },
    { label: `🧮 ${t('ai.suggestion_payroll_calc')}`, text: t('ai.suggestion_payroll_text') },
  ];

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [attachedFile, setAttachedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const chatRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages, loading]);

  function handleFileSelect(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setAttachedFile(file);
    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setFilePreview(url);
    } else {
      setFilePreview(null);
    }
    e.target.value = '';
  }

  function removeFile() {
    setAttachedFile(null);
    if (filePreview) {
      URL.revokeObjectURL(filePreview);
      setFilePreview(null);
    }
  }

  async function toggleMic() {
    if (listening) {
      // Stop recording
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      setListening(false);
      return;
    }

    // Start recording
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeTypes = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/ogg', 'audio/mp4'];
      const mimeType = mimeTypes.find(t => MediaRecorder.isTypeSupported(t)) || '';

      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      const chunks = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      recorder.onstop = async () => {
        // Stop all tracks
        stream.getTracks().forEach(t => t.stop());
        streamRef.current = null;

        if (chunks.length === 0) return;

        const ext = (recorder.mimeType || '').includes('ogg') ? 'ogg' : 'webm';
        const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' });

        setTranscript(`⏳ ${t('ai.transcribing')}`);
        try {
          const form = new FormData();
          form.append('file', blob, `voice.${ext}`);
          const { data } = await api.post('/ai/transcribe', form, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 30000,
          });
          setTranscript('');
          if (data.text && data.text.trim()) {
            send(data.text.trim());
          }
        } catch (e) {
          setTranscript('');
          alert(t('ai.recognition_error') + ': ' + (e.response?.data?.detail || e.message));
        }
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setListening(true);
      setTranscript('');
    } catch (e) {
      alert(t('ai.no_mic_access') + ': ' + e.message);
    }
  }

  async function send(text) {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    const currentFile = attachedFile;
    const currentPreview = filePreview;
    setInput('');
    setTranscript('');
    setAttachedFile(null);
    setFilePreview(null);

    const userMsg = { role: 'user', content: msg };
    if (currentFile) {
      userMsg.file = { name: currentFile.name, type: currentFile.type };
      if (currentPreview) userMsg.file.preview = currentPreview;
    }
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    try {
      let data;
      if (currentFile) {
        const form = new FormData();
        form.append('message', msg);
        if (sessionId) form.append('session_id', sessionId);
        form.append('file', currentFile);
        const res = await api.post('/ai/assistant/with-file', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000,
        });
        data = res.data;
      } else {
        const res = await api.post('/ai/assistant', {
          message: msg,
          session_id: sessionId,
        }, { timeout: 120000 });
        data = res.data;
      }
      setSessionId(data.session_id);
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: data.reply, actions: data.actions || [] },
      ]);
    } catch (e) {
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: '⚠️ ' + t('common.error') + ': ' + (e.response?.data?.detail || e.message) },
      ]);
    } finally {
      setLoading(false);
    }
  }

  function ActionCard({ action }) {
    const typeLabels = {
      proforma: `📄 ${t('ai.action_proforma')}`,
      invoice: `🧾 ${t('ai.action_invoice')}`,
      employee: `👤 ${t('ai.action_employee')}`,
      termination: `🔴 ${t('ai.action_termination')}`,
      payout: `💸 ${t('ai.action_payout')}`,
      leave: `🏖 ${t('ai.action_leave')}`,
    };

    return (
      <div style={{
        background: 'linear-gradient(135deg, rgba(99,102,241,0.2), rgba(236,72,153,0.1))',
        border: '1px solid rgba(99,102,241,0.3)',
        borderRadius: 12, padding: 12, marginTop: 8,
      }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>
          {typeLabels[action.type] || `✅ ${t('ai.action_default')}`}
        </div>
        {action.number && <div style={{ fontSize: 12, opacity: 0.7 }}>№ {action.number}</div>}
        {action.name && <div>👤 {action.name}</div>}
        {action.buyer && <div>🏢 {action.buyer}</div>}
        {action.gross != null && <div>💰 {action.gross} {action.currency || 'PLN'}</div>}
        {action.amount != null && <div>💰 {action.amount} PLN</div>}
        {action.contract_type && <div>📋 {action.contract_type}</div>}
        {action.gross_salary != null && <div>💵 {action.gross_salary} PLN брутто</div>}
        {action.start_date && <div>📅 с {action.start_date}</div>}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#0f0f23' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #6366f1, #ec4899)',
        padding: '16px 20px', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => setPage('dashboard')} style={{
            background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8,
            color: '#fff', padding: '6px 10px', cursor: 'pointer', fontSize: 16,
          }}>←</button>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>🤖 {t('ai.assistant_title')}</div>
            <div style={{ fontSize: 12, opacity: 0.8 }}>{t('ai.assistant_subtitle')}</div>
          </div>
        </div>
      </div>

      {/* Chat area */}
      <div ref={chatRef} style={{
        flex: 1, overflowY: 'auto', padding: 16,
        display: 'flex', flexDirection: 'column', gap: 12,
      }}>
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', marginTop: 40 }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🤖</div>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
              {t('ai.assistant_welcome')}
            </div>
            <div style={{ fontSize: 13, opacity: 0.6, marginBottom: 24 }}>
              {t('ai.assistant_hint')}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
              {SUGGESTIONS.map(s => (
                <button key={s.label} onClick={() => send(s.text)} style={{
                  background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)',
                  borderRadius: 20, padding: '8px 14px', color: '#a5b4fc',
                  fontSize: 13, cursor: 'pointer', transition: 'all 0.2s',
                }}>{s.label}</button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} style={{
            display: 'flex',
            justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
            animation: 'fadeIn 0.3s ease',
          }}>
            <div style={{
              maxWidth: '85%',
              background: msg.role === 'user'
                ? 'linear-gradient(135deg, #6366f1, #8b5cf6)'
                : 'rgba(255,255,255,0.08)',
              borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
              padding: '10px 14px',
              border: msg.role === 'user' ? 'none' : '1px solid rgba(255,255,255,0.1)',
            }}>
              {msg.file && (
                <div style={{ marginBottom: 6 }}>
                  {msg.file.preview ? (
                    <img src={msg.file.preview} style={{ maxWidth: 200, borderRadius: 8, display: 'block' }} />
                  ) : (
                    <div style={{
                      background: 'rgba(255,255,255,0.1)', borderRadius: 8,
                      padding: '6px 10px', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4,
                    }}>📄 {msg.file.name}</div>
                  )}
                </div>
              )}
              <div style={{ fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                {msg.content}
              </div>
              {msg.actions?.map((a, j) => <ActionCard key={j} action={a} />)}
            </div>
          </div>
        ))}

        {loading && (
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <div style={{
              background: 'rgba(255,255,255,0.08)', borderRadius: 16,
              padding: '10px 14px', border: '1px solid rgba(255,255,255,0.1)',
            }}>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                {[0, 1, 2].map(i => (
                  <div key={i} style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: '#6366f1',
                    animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite`,
                  }} />
                ))}
              </div>
            </div>
          </div>
        )}

        {transcript && (
          <div style={{
            textAlign: 'center', fontSize: 13, color: '#a5b4fc',
            fontStyle: 'italic', padding: 8,
          }}>
            🎤 {transcript}
          </div>
        )}
      </div>

      {/* Input area */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.pdf"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
      {attachedFile && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '6px 16px',
          background: 'rgba(99,102,241,0.15)',
          borderBottom: '1px solid rgba(99,102,241,0.3)',
          flexShrink: 0,
        }}>
          {filePreview ? (
            <img src={filePreview} style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6 }} />
          ) : (
            <span>📄</span>
          )}
          <span style={{ fontSize: 12, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#a5b4fc' }}>{attachedFile.name}</span>
          <button onClick={removeFile} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>
      )}
      <div style={{
        padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.1)',
        display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0,
        background: 'rgba(15,15,35,0.95)',
      }}>
        <button onClick={() => fileInputRef.current?.click()} style={{
          width: 48, height: 48, borderRadius: '50%',
          background: attachedFile
            ? 'linear-gradient(135deg, #10b981, #059669)'
            : 'rgba(255,255,255,0.1)',
          border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer',
          flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>📎</button>

        <button onClick={toggleMic} style={{
          width: 48, height: 48, borderRadius: '50%',
          background: listening
            ? 'linear-gradient(135deg, #ef4444, #f97316)'
            : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
          border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer',
          flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          animation: listening ? 'micPulse 1.5s ease-in-out infinite' : 'none',
          boxShadow: listening ? '0 0 20px rgba(239,68,68,0.5)' : '0 0 10px rgba(99,102,241,0.3)',
        }}>🎤</button>

        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && send()}
          placeholder={t('ai.input_placeholder')}
          style={{
            flex: 1, background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 24, padding: '10px 16px',
            color: '#fff', fontSize: 14, outline: 'none',
          }}
        />

        <button onClick={() => send()} disabled={loading || !input.trim()} style={{
          width: 48, height: 48, borderRadius: '50%',
          background: input.trim() ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : 'rgba(255,255,255,0.1)',
          border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer',
          flexShrink: 0, opacity: input.trim() ? 1 : 0.5,
        }}>➤</button>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.5; }
          40% { transform: scale(1); opacity: 1; }
        }
        @keyframes micPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
      `}</style>
    </div>
  );
}
